import { useEffect, useRef, useCallback } from 'react';

interface UseInfiniteScrollOptions {
  hasMore: boolean;
  isLoading: boolean;
  onLoadMore: () => void;
  threshold?: number; // Distance from bottom to trigger load (in pixels) - used in scroll version
  rootMargin?: string; // Intersection observer root margin
  enabled?: boolean; // Whether infinite scroll is enabled
}

/**
 * Custom hook for infinite scroll functionality
 * Uses Intersection Observer API for performance optimization
 */
export const useInfiniteScroll = ({
  hasMore,
  isLoading,
  onLoadMore,
  threshold: _threshold = 200, // Prefixed with underscore to indicate it's not used in this version
  rootMargin = '0px',
  enabled = true
}: UseInfiniteScrollOptions) => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      
      // Only trigger load more if:
      // - Element is intersecting (visible)
      // - We have more content to load
      // - We're not currently loading
      // - Infinite scroll is enabled
      if (entry.isIntersecting && hasMore && !isLoading && enabled) {
        onLoadMore();
      }
    },
    [hasMore, isLoading, onLoadMore, enabled]
  );

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    // Create intersection observer with performance optimizations
    observerRef.current = new IntersectionObserver(handleIntersection, {
      root: null, // Use viewport as root
      rootMargin, // Margin around root
      threshold: 0.1 // Trigger when 10% of sentinel is visible
    });

    observerRef.current.observe(sentinel);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [handleIntersection, rootMargin]);

  // Cleanup observer on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return { sentinelRef };
};

/**
 * Alternative scroll-based infinite scroll hook
 * Uses scroll events with throttling for performance
 */
export const useScrollInfiniteScroll = ({
  hasMore,
  isLoading,
  onLoadMore,
  threshold = 200,
  enabled = true
}: Omit<UseInfiniteScrollOptions, 'rootMargin'>) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const throttleRef = useRef<NodeJS.Timeout | null>(null);

  const handleScroll = useCallback(() => {
    if (!enabled || !hasMore || isLoading) return;

    const container = containerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    if (distanceFromBottom <= threshold) {
      onLoadMore();
    }
  }, [hasMore, isLoading, onLoadMore, threshold, enabled]);

  const throttledHandleScroll = useCallback(() => {
    if (throttleRef.current) {
      clearTimeout(throttleRef.current);
    }
    
    throttleRef.current = setTimeout(handleScroll, 100); // Throttle to 100ms
  }, [handleScroll]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('scroll', throttledHandleScroll, { passive: true });

    return () => {
      container.removeEventListener('scroll', throttledHandleScroll);
      if (throttleRef.current) {
        clearTimeout(throttleRef.current);
      }
    };
  }, [throttledHandleScroll]);

  return { containerRef };
};